import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { pathLabel, type LocationNode } from "@iltocat/shared";
import { plural } from "../lib/api.js";
import type { BulkFailure } from "../lib/bulkItems.js";
import { DRAG_TYPE, ancestorIds, dragIds, subtreeIds } from "../lib/locationMoves.js";
import { useItemView } from "../lib/useItemView.js";
import { useItems } from "../lib/useItems.js";
import { useMover } from "../lib/useMover.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { BulkBar } from "../components/BulkBar.js";
import { CategoryFilter } from "../components/CategoryFilter.js";
import { Dialog } from "../components/Dialog.js";
import { EmptyState } from "../components/EmptyState.js";
import { LoadMore } from "../components/LoadMore.js";
import { Icon } from "../components/Icon.js";
import { ItemGrid, ItemViewToggle } from "../components/ItemGrid.js";
import { LocationTree } from "../components/LocationTree.js";
import { Menu, MenuItem } from "@vcnet/ui";
import { MoveDialog } from "../components/MoveDialog.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { useToast } from "../components/Toast.js";

/**
 * The shelf view: walking the basement without going down there.
 *
 * One node at a time, with its children as doors and its own items as a grid,
 * rather than a whole tree expanded on one page, which is the shape that stops
 * working at the third shelf. A child's count includes everything below it,
 * because "Shelf A: 40" is the useful number when deciding whether to open it;
 * the items shown are only the ones filed in *this* node, because that is what
 * being in a bin means.
 *
 * On a desktop the whole tree sits beside it, folded, as somewhere to jump to
 * and somewhere to drop things: drag an item (or everything ticked) onto a
 * location in the tree to move it there. A phone has no drag, so there the
 * same move is Select, then Move; and the unfiled pile has its own screen for
 * sorting one thing at a time.
 *
 * A location itself is renamed, moved or deleted from the menu on its card
 * in the list, or from the menu on its own screen, and on a desktop moved by
 * dragging it onto another in the tree. The actions used to be only on its
 * own screen, where nobody looked for them. Deleting one with anything in it
 * asks where its items and the locations inside it go: up into its parent,
 * the top level, or anywhere else (`DeleteLocationDialog`).
 */

export function Locations({
  id,
  taxonomy,
  vocabulary,
  onOpen,
  onGo,
  onLabels,
  onSort,
  stock = false,
}: {
  id: string | null;
  /** A stockroom: its items start as a list, since few of them have photos. */
  stock?: boolean;
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  onOpen: (itemId: string) => void;
  onGo: (locationId: string | null) => void;
  onLabels: (locationId: string) => void;
  /** The sort screen, for the unfiled pile. */
  onSort: () => void;
}) {
  const here = id ? (taxonomy.byId.get(id) ?? null) : null;
  const children = here ? here.children : taxonomy.tree;

  // Every step above this one, root first, so each crumb can be a link.
  const ancestors = useMemo(
    () => ancestorIds(taxonomy.byId, id).flatMap((step) => taxonomy.byId.get(step) ?? []),
    [id, taxonomy.byId],
  );

  const [category, setCategory] = useState<string | null | undefined>(undefined);
  const query = useMemo(() => {
    const where = id ? { locationId: id } : { locationId: null };
    return category === undefined ? where : { ...where, categoryId: category };
  }, [id, category]);
  const list = useItems(query);
  const [view, chooseView] = useItemView(stock);

  const [adding, setAdding] = useState<string | null>(null);
  /** The location being renamed, moved or deleted, from its card or its own screen. */
  const [acting, setActing] = useState<{ kind: "rename" | "move" | "delete"; node: LocationNode } | null>(
    null,
  );
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<BulkFailure[]>([]);

  // The tree opens itself as far as the location on screen, and otherwise
  // stays however it was left.
  const [open, setOpen] = useState<Set<string>>(() => new Set(ancestorIds(taxonomy.byId, id)));
  useEffect(() => {
    const path = ancestorIds(taxonomy.byId, id);
    if (id) path.push(id);
    setOpen((current) =>
      path.every((step) => current.has(step)) ? current : new Set([...current, ...path]),
    );
  }, [id, taxonomy.byId]);

  // Only a mouse or a trackpad drags: a long press on a phone is a scroll or
  // a menu, and the tree it would be dragged to is not on a phone's screen.
  const [canDrag] = useState(() => window.matchMedia("(pointer: fine)").matches);
  const ghost = useRef<HTMLDivElement>(null);

  const whereIs = useCallback(
    (itemId: string) => (list.items.some((item) => item.id === itemId) ? id : undefined),
    [list.items, id],
  );
  const mover = useMover({ taxonomy, whereIs, onDone: list.reload });

  // A different location is a different set of items; ticks do not follow.
  useEffect(() => {
    setPicked(new Set());
    setFailed([]);
  }, [id, category]);

  useEffect(() => {
    if (!selecting) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") stopSelecting();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selecting]);

  function stopSelecting() {
    setSelecting(false);
    setPicked(new Set());
  }

  const toggle = (itemId: string) =>
    setPicked((now) => {
      const next = new Set(now);
      if (!next.delete(itemId)) next.add(itemId);
      return next;
    });

  function startDrag(itemId: string, event: DragEvent<HTMLElement>) {
    const ids = dragIds(picked, itemId);
    event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(ids));
    event.dataTransfer.effectAllowed = "move";
    if (ghost.current) {
      const title = list.items.find((item) => item.id === itemId)?.title || "Untitled item";
      ghost.current.textContent = ids.length > 1 ? `${ids.length} items` : title;
      event.dataTransfer.setDragImage(ghost.current, 16, 16);
    }
  }

  async function drop(to: string | null, ids: string[]) {
    if (to === id) return;
    setFailed([]);
    const failures = await mover.move(ids, to);
    setFailed(failures);
    const stuck = new Set(failures.map((failure) => failure.id));
    setPicked((now) => new Set([...now].filter((one) => stuck.has(one) || !ids.includes(one))));
  }

  async function add() {
    const name = (adding ?? "").trim();
    if (!name) return;

    setBusy(true);
    setError(null);
    try {
      await taxonomy.addLocation(name, id);
      setAdding(null);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That location could not be added.");
    } finally {
      setBusy(false);
    }
  }

  const nameOf = (location: string | null) =>
    location ? (taxonomy.byId.get(location)?.name ?? "that location") : "the top level";

  /** Runs one change to a location, putting its refusal (a sentence from the route) on screen. */
  async function changing(work: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await work();
      return true;
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That location could not be changed.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function rename(node: LocationNode, typed: string) {
    const before = node.name;
    const name = typed.trim();
    if (!name || name === before) return setActing(null);
    const target = node.id;
    if (await changing(() => taxonomy.updateLocation(target, { name }))) {
      setActing(null);
      toast(`Renamed to ${name}.`, {
        label: "Undo",
        run: () => void changing(() => taxonomy.updateLocation(target, { name: before })),
      });
    }
  }

  /** Moves a location inside another (null: the top level), with Undo. */
  async function relocate(moving: string, into: string | null) {
    const node = taxonomy.byId.get(moving);
    if (!node || node.parentId === into) return;
    const before = node.parentId;
    if (await changing(() => taxonomy.updateLocation(moving, { parentId: into }))) {
      toast(into ? `Moved ${node.name} into ${nameOf(into)}.` : `Moved ${node.name} to the top level.`, {
        label: "Undo",
        run: () => void changing(() => taxonomy.updateLocation(moving, { parentId: before })),
      });
    }
  }

  /** Deletes a location, its contents going `into` a location, the top level (null) or its parent. */
  async function remove(node: LocationNode, into: string | null) {
    const { id: gone, name, parentId } = node;
    if (await changing(() => taxonomy.deleteLocation(gone, into))) {
      setActing(null);
      toast(`Deleted ${name}.`);
      // Off a screen that no longer exists, to where its things went.
      if (gone === id || (id && ancestorIds(taxonomy.byId, id).includes(gone))) onGo(into ?? parentId);
    }
  }

  /** The Rename / Move to / Delete menu, on a card or the screen's own header. */
  const actionsMenu = (node: LocationNode, label: string, bordered: boolean) => (
    <Menu
      label={label}
      buttonClassName={bordered ? "icon-button icon-button-bordered" : "icon-button place-more"}
      button={<Icon name="more" size={20} />}
    >
      {(close) => (
        <>
          <MenuItem
            icon={<Icon name="pencil" />}
            onClick={() => {
              close();
              setActing({ kind: "rename", node });
            }}
          >
            Rename
          </MenuItem>
          <MenuItem
            icon={<Icon name="folder" />}
            onClick={() => {
              close();
              setActing({ kind: "move", node });
            }}
          >
            Move to…
          </MenuItem>
          <MenuItem
            danger
            icon={<Icon name="trash" />}
            onClick={() => {
              close();
              setActing({ kind: "delete", node });
            }}
          >
            Delete…
          </MenuItem>
        </>
      )}
    </Menu>
  );

  const titleOf = (itemId: string) => list.items.find((item) => item.id === itemId)?.title || "Untitled item";
  const unfiledOnScreen = !here && category === undefined ? list.total : undefined;

  return (
    <div className="shelf">
      <aside className="shelf-tree" aria-label="All locations">
        <p className="shelf-tree-head">
          All locations
          {canDrag && (
            <span className="shelf-tree-hint">Drag items here to move them, or a location onto another</span>
          )}
        </p>
        <LocationTree
          tree={taxonomy.tree}
          current={id}
          open={open}
          unfiled={{
            label: "Not filed",
            ...(unfiledOnScreen !== undefined ? { count: unfiledOnScreen } : {}),
          }}
          onToggle={(node, now) =>
            setOpen((current) => {
              const next = new Set(current);
              if (now) next.add(node);
              else next.delete(node);
              return next;
            })
          }
          onPick={onGo}
          onDropItems={(to, ids) => void drop(to, ids)}
          onDropLocation={(moving, into) => void relocate(moving, into)}
        />
      </aside>

      <div className="shelf-main">
        {here && (
          <nav className="crumbs" aria-label="Where this is">
            <button type="button" className="crumb" onClick={() => onGo(null)}>
              Locations
            </button>
            {ancestors.map((step) => (
              <span key={step.id} className="crumb-step">
                <Icon name="chevron-right" size={14} />
                <button type="button" className="crumb" onClick={() => onGo(step.id)}>
                  {step.name}
                </button>
              </span>
            ))}
          </nav>
        )}

        <PageHeader
          title={here ? here.name : "Locations"}
          meta={here ? <span>{plural(here.totalCount, "item")} in here</span> : undefined}
          actions={
            adding === null ? (
              <>
                {here && (
                  <button type="button" className="button button-quiet" onClick={() => onLabels(here.id)}>
                    <Icon name="qr" size={18} />
                    <span>Labels</span>
                  </button>
                )}
                <button type="button" className="button button-quiet" onClick={() => setAdding("")}>
                  <Icon name="plus" size={18} />
                  {here ? "Add inside" : "Add location"}
                </button>
                {here && actionsMenu(here, "More actions", true)}
              </>
            ) : undefined
          }
        />

        {acting?.kind === "rename" && (
          <RenameLocationDialog
            node={acting.node}
            busy={busy}
            onRename={(name) => void rename(acting.node, name)}
            onClose={() => setActing(null)}
          />
        )}

        {acting?.kind === "move" && (
          <MoveDialog
            title={`Move ${acting.node.name} into…`}
            taxonomy={taxonomy}
            here={acting.node.parentId}
            hidden={subtreeIds(acting.node)}
            unfiledLabel="Top level"
            onChoose={(into) => {
              const moving = acting.node.id;
              setActing(null);
              void relocate(moving, into);
            }}
            onClose={() => setActing(null)}
          />
        )}

        {acting?.kind === "delete" && (
          <DeleteLocationDialog
            node={acting.node}
            taxonomy={taxonomy}
            busy={busy}
            error={error}
            onDelete={(into) => void remove(acting.node, into)}
            onClose={() => {
              setActing(null);
              setError(null);
            }}
          />
        )}

        {adding !== null && (
          <form
            className="inline-add"
            onSubmit={(event) => {
              event.preventDefault();
              void add();
            }}
          >
            <Icon name="folder" size={18} />
            <input
              className="input"
              type="text"
              autoFocus
              value={adding}
              placeholder={here ? `Inside ${here.name}` : "Garage, Basement, Office…"}
              aria-label={here ? `New location inside ${here.name}` : "New location"}
              onChange={(event) => setAdding(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setAdding(null);
              }}
            />
            <button type="submit" className="button" disabled={busy || adding.trim().length === 0}>
              Add
            </button>
            <button type="button" className="button button-ghost" onClick={() => setAdding(null)}>
              Cancel
            </button>
          </form>
        )}

        {error && <Notice tone="error">{error}</Notice>}

        {children.length > 0 ? (
          <ul className="places">
            {children.map((child) => (
              <li key={child.id} className="place-row">
                <button type="button" className="place" onClick={() => onGo(child.id)}>
                  <span className="place-icon">
                    <Icon name="folder" size={20} />
                  </span>
                  <span className="place-text">
                    <span className="place-name">{child.name}</span>
                    <span className="place-count">{countOf(child)}</span>
                  </span>
                  <Icon name="chevron-right" size={18} className="place-go" />
                </button>
                {actionsMenu(child, `Actions for ${child.name}`, false)}
              </li>
            ))}
          </ul>
        ) : (
          !here &&
          adding === null && (
            <EmptyState
              icon="pin"
              title="No locations yet"
              action={
                <button type="button" className="button" onClick={() => setAdding("")}>
                  <Icon name="plus" size={18} />
                  Add the first one
                </button>
              }
            >
              Rooms, shelves, bins. Nest them as deep as you like.
            </EmptyState>
          )
        )}

        <section className="section">
          <header className="section-head">
            <h2 className="section-title">{here ? "Filed here" : "Not filed anywhere"}</h2>
            <div className="section-tools">
              {!here && list.total > 0 && !selecting && (
                <button type="button" className="button button-small" onClick={onSort}>
                  <Icon name="zap" size={16} />
                  Sort these
                </button>
              )}
              <CategoryFilter categories={taxonomy.categories} value={category} onChange={setCategory} />
              <ItemViewToggle value={view} onChange={chooseView} />
              {(selecting || list.items.length > 0) && (
                <button
                  type="button"
                  className={
                    selecting
                      ? "button button-quiet button-small button-on"
                      : "button button-quiet button-small"
                  }
                  aria-pressed={selecting}
                  onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
                >
                  <Icon name="check-circle" size={16} />
                  {selecting ? "Done" : "Select"}
                </button>
              )}
            </div>
          </header>

          {list.error && <Notice tone="error">{list.error}</Notice>}
          {!selecting && failed.length > 0 && (
            <Notice tone="error">
              {`${plural(failed.length, "item")} did not move: ${failed
                .map((failure) => `${titleOf(failure.id)} (${failure.message})`)
                .join("; ")}`}
            </Notice>
          )}

          {list.loading ? (
            <ul className="grid" aria-busy="true" aria-label="Loading">
              {Array.from({ length: 4 }, (_, index) => (
                <li key={index} className="card card-skeleton" />
              ))}
            </ul>
          ) : list.items.length === 0 ? (
            <p className="muted section-empty">
              {here ? "Nothing is filed directly here." : "Everything has a home."}
            </p>
          ) : (
            <>
              <ItemGrid
                items={list.items}
                dense={view === "list"}
                locationOf={() => (here ? pathLabel(here.path) : "")}
                onOpen={onOpen}
                {...(selecting ? { selection: { picked, onToggle: toggle } } : {})}
                {...(canDrag ? { onDragItem: startDrag } : {})}
              />
              <LoadMore more={list.more} loading={list.loadingMore} onMore={list.showMore} />
            </>
          )}

          {selecting && (
            <BulkBar
              items={list.items}
              total={list.total}
              picked={picked}
              taxonomy={taxonomy}
              vocabulary={vocabulary}
              stock={stock}
              here={id}
              onSelectAll={async () => {
                const all = await list.loadAll();
                if (all) setPicked(new Set(all.map((item) => item.id)));
              }}
              onClear={(keep) => setPicked(new Set(keep ?? []))}
              onDone={list.reload}
            />
          )}
        </section>
      </div>

      <div ref={ghost} className="drag-ghost" aria-hidden="true" />
    </div>
  );
}

/** "12 items" for a leaf; "12 items, 40 below" for a shelf full of bins. */
function countOf(node: LocationNode): string {
  const below = node.totalCount - node.itemCount;
  const own = plural(node.itemCount, "item");
  return below > 0 ? `${own}, ${below} below` : own;
}

function RenameLocationDialog({
  node,
  busy,
  onRename,
  onClose,
}: {
  node: LocationNode;
  busy: boolean;
  onRename: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(node.name);
  return (
    <Dialog title={`Rename ${node.name}`} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          onRename(name);
        }}
      >
        <label className="field">
          <span className="field-label">New name</span>
          <input
            className="input"
            // Typed straight after choosing Rename.
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy || name.trim().length === 0}>
            Rename
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Deleting a location, saying what is in it and asking where that goes: up
 * into its parent (the default, and what deleting always did), the top level
 * (items become unfiled), or any other location. Nothing in it: one button.
 */
function DeleteLocationDialog({
  node,
  taxonomy,
  busy,
  error,
  onDelete,
  onClose,
}: {
  node: LocationNode;
  taxonomy: Taxonomy;
  busy: boolean;
  error: string | null;
  onDelete: (into: string | null) => void;
  onClose: () => void;
}) {
  const parent = node.parentId ? taxonomy.byId.get(node.parentId) : undefined;
  const [where, setWhere] = useState<"parent" | "top" | "other">(parent ? "parent" : "top");
  const [other, setOther] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);

  const items = node.totalCount;
  const places = node.children.length;
  const empty = items === 0 && places === 0;
  const contents = [items > 0 && plural(items, "item"), places > 0 && plural(places, "location")]
    .filter(Boolean)
    .join(" and ");
  const into = where === "parent" ? (parent?.id ?? null) : where === "top" ? null : other;
  const otherName = other ? (taxonomy.byId.get(other)?.name ?? "that location") : null;

  if (choosing) {
    return (
      <MoveDialog
        title={`Move what is in ${node.name} into…`}
        taxonomy={taxonomy}
        here={null}
        hidden={subtreeIds(node)}
        unfiledLabel="Top level"
        onChoose={(to) => {
          setChoosing(false);
          if (to === null) return setWhere("top");
          setOther(to);
          setWhere("other");
        }}
        onClose={() => setChoosing(false)}
      />
    );
  }

  return (
    <Dialog title={`Delete ${node.name}?`} onClose={onClose}>
      <div className="stack">
        {empty ? (
          <p className="muted">Nothing is filed in it.</p>
        ) : (
          <fieldset className="field delete-where">
            <legend className="field-label">
              {`${contents.charAt(0).toUpperCase()}${contents.slice(1)} ${items + places === 1 ? "is" : "are"} inside. Move ${items + places === 1 ? "it" : "them"} to:`}
            </legend>
            {parent && (
              <label className="radio-row">
                <input type="radio" checked={where === "parent"} onChange={() => setWhere("parent")} />
                <span>
                  Up into <strong>{parent.name}</strong>
                </span>
              </label>
            )}
            <label className="radio-row">
              <input type="radio" checked={where === "top"} onChange={() => setWhere("top")} />
              <span>
                The top level
                {items > 0 && <span className="muted small"> (items are left unfiled)</span>}
              </span>
            </label>
            <label className="radio-row">
              <input
                type="radio"
                checked={where === "other"}
                onChange={() => (other ? setWhere("other") : setChoosing(true))}
              />
              <span>
                {otherName ? (
                  <>
                    <strong>{otherName}</strong>{" "}
                  </>
                ) : (
                  "Somewhere else… "
                )}
                <button type="button" className="link" onClick={() => setChoosing(true)}>
                  {otherName ? "Change" : "Pick a location"}
                </button>
              </span>
            </label>
          </fieldset>
        )}
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button button-danger"
            disabled={busy || (where === "other" && !other)}
            onClick={() => onDelete(into)}
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
